import React, { useState, useEffect, Suspense, lazy } from "react";
import { Routes, Route, Navigate, useLocation } from "react-router-dom";
import { useIsAuthenticated, useMsal } from "@azure/msal-react";
import { InteractionStatus, EventType } from "@azure/msal-browser";
import { DataProvider } from "./DataContext";
import { gf, MANAGEMENT_ON_TRACK, MANAGEMENT_GROWTH, MANAGEMENT_STRATEGIC } from "./fieldConfig";
import LoginPage from "./pages/LoginPage";
import PmLayout from "./pages/projects/PmLayout";

// Route-level code splitting — these used to be eager imports, which meant
// every page's code (Board, Gantt, Budget, Automation, Permissions, ...)
// shipped in the one main bundle even if a session never opens most of
// them. Lazy-loading means the browser only fetches a page's chunk the
// first time that route is actually visited.
const PeopleAdminPage = lazy(() => import("./pages/PeopleAdminPage"));
const SupplierPoolPage = lazy(() => import("./pages/SupplierPoolPage"));
const TeamCalendar = lazy(() => import("./pages/TeamCalendar"));
const DemoWorkflowPage = lazy(() => import("./pages/demo/DemoWorkflowPage"));
const SalesImportPage = lazy(() => import("./pages/sales/SalesImportPage"));
const CustomerDetailPage = lazy(() => import("./pages/sales/CustomerDetailPage"));
const SalesAnalyticsPage = lazy(() => import("./pages/sales/SalesAnalyticsPage"));
const ProductPerformancePage = lazy(() => import("./pages/sales/ProductPerformancePage"));
const SalesImportAuditPage = lazy(() => import("./pages/sales/SalesImportAuditPage"));
const SecretaryRoom = lazy(() => import("./pages/SecretaryRoom"));
const LiveReportLayout = lazy(() => import("./pages/LiveReportLayout"));
const ReceivedWorkCalendar = lazy(() => import("./pages/ReceivedWorkCalendar"));
const FollowUpStatistics = lazy(() => import('./pages/FollowUpStatistics.jsx'));
const FollowUpTestPage = lazy(() => import('./pages/FollowUpTestPage.jsx'));
const LiveReportDashboard = lazy(() => import("./pages/Dashboard"));
const PersonRequests = lazy(() => import("./pages/PersonRequests"));
const PersonSupport = lazy(() => import("./pages/PersonSupport"));
const WorkOverview = lazy(() => import("./pages/WorkOverview"));
const ManagementTypePage = lazy(() => import("./pages/ManagementTypePage"));
const TaskTablePage = lazy(() => import("./pages/TaskTablePage"));
const AllTasksPage = lazy(() => import("./pages/AllTasksPage"));
const LiveReportSettingsPage = lazy(() => import("./pages/LiveReportSettingsPage"));
const LiveReportFollowUpUpdatePage = lazy(() => import("./pages/LiveReportFollowUpUpdatePage"));
const ProjectHomePage = lazy(() => import("./pages/projects/ProjectHomePage"));
const ProjectOverviewPage = lazy(() => import("./pages/projects/ProjectOverviewPage"));
const BacklogPage = lazy(() => import("./pages/projects/BacklogPage"));
const BoardPage = lazy(() => import("./pages/projects/BoardPage"));
const SprintPlanningPage = lazy(() => import("./pages/projects/SprintPlanningPage"));
const GanttPage = lazy(() => import("./pages/projects/GanttPage"));
const ScheduleImportPage = lazy(() => import("./pages/projects/ScheduleImportPage"));
const BudgetPage = lazy(() => import("./pages/projects/BudgetPage"));
const BudgetReportPage = lazy(() => import("./pages/projects/BudgetReportPage"));
const ProjectSettingsPage = lazy(() => import("./pages/projects/ProjectSettingsPage"));
const AuditLogPage = lazy(() => import("./pages/projects/AuditLogPage"));
const WorkflowSettingsPage = lazy(() => import("./pages/projects/WorkflowSettingsPage"));
const WorkloadPage = lazy(() => import("./pages/projects/WorkloadPage"));
const AutomationSettingsPage = lazy(() => import("./pages/projects/AutomationSettingsPage"));
const PermissionsSettingsPage = lazy(() => import("./pages/projects/PermissionsSettingsPage"));
const ArchivedProjectsPage = lazy(() => import("./pages/projects/ArchivedProjectsPage"));
const TemplatesPage = lazy(() => import("./pages/projects/TemplatesPage"));
const TeamsSettingsPage = lazy(() => import("./pages/projects/TeamsSettingsPage"));
const ResourceWorkloadPage = lazy(() => import("./pages/projects/ResourceWorkloadPage"));
const ResourceSettingsPage = lazy(() => import("./pages/projects/ResourceSettingsPage"));
const MyDashboardPage = lazy(() => import("./pages/me/MyDashboardPage"));
const MyCalendarPage = lazy(() => import("./pages/me/MyCalendarPage"));
const MyTasksPage = lazy(() => import("./pages/me/MyTasksPage"));
const MySettingsPage = lazy(() => import("./pages/me/MySettingsPage"));
const PreProjectControlPage = lazy(() => import("./pages/preProject/PreProjectControlPage"));

function RouteFallback() {
  return (
    <div style={{ display: "flex", alignItems: "center", justifyContent: "center", height: "60vh" }}>
      <div style={{
        width: 32, height: 32, border: "4px solid #E0E6ED", borderTopColor: "#1B4F9B",
        borderRadius: "50%", animation: "spin 0.8s linear infinite",
      }} />
      <style>{`@keyframes spin { to { transform: rotate(360deg); } }`}</style>
    </div>
  );
}

// React error #300 — เคยคิดว่ามาจาก recharts แต่จริงๆคือ msal-react internal
// setState ชนตอน component อื่นกำลัง render อยู่พอดี (ดู memory
// project_react_error_300_saga) ปัญหาที่ทำให้ "รีโหลดหน้า" (remount ในตัวแอป)
// ไม่ช่วยเลย ต้องปิด tab เปิดใหม่ถึงจะกลับมาใช้ได้: msal-browser ใช้
// sessionStorage เก็บ "interaction lock" (key: msal.interaction.status) เสมอ
// ไม่ว่า cache.cacheLocation จะตั้งเป็นอะไร — ถ้า error ไปขัดจังหวะ MSAL ตอน
// กำลัง acquireToken อยู่ lock นี้จะค้างเป็น true ตลอดไป ทำให้ทุก
// acquireTokenSilent/Popup ครั้งต่อไป throw "interaction_in_progress" วนซ้ำ
// remount ในตัวแอปไม่ได้แตะ sessionStorage เลยจึงไม่ช่วย — ต้องเคลียร์ key นี้ทิ้ง
// ก่อน ถึงจะกลับมาทำงานได้โดยไม่ต้องปิด tab จริง
const RECHARTS_ERROR_PATTERN = /Minified React error #300/;
const RELOAD_GUARD_KEY = "dtc:lastAutoReload";
const MSAL_INTERACTION_LOCK_KEY = "msal.interaction.status";
const DEMO_IMPORT_REVIEW_KEY = "demo:pendingImportReview";
function clearMsalInteractionLock() {
  try { sessionStorage.removeItem(MSAL_INTERACTION_LOCK_KEY); } catch { /* ignore */ }
}
function clearRecoverableUiState() {
  clearMsalInteractionLock();
  try { localStorage.removeItem(DEMO_IMPORT_REVIEW_KEY); } catch { /* ignore */ }
}

class ErrorBoundary extends React.Component {
  constructor(props) { super(props); this.state = { error: null, autoRemounting: false }; }
  static getDerivedStateFromError(error) { return { error }; }
  componentDidCatch(error, info) {
    console.error("[ErrorBoundary]", error, info);
    if (RECHARTS_ERROR_PATTERN.test(error?.message ?? "")) {
      // remount (setState) อย่างเดียวไม่พอ — MsalProvider อยู่เหนือ ErrorBoundary
      // ใน main.jsx สถานะ interaction ค้างของมันไม่ถูกรีเซ็ตด้วย remount ในแอป
      // ต้อง reload จริงถึงจะเคลียร์ ตอนนี้เคลียร์ sessionStorage lock ก่อน reload
      // เสมอแล้ว (commit ec82427) จึงไม่ต้องกลัวหลุด session เหมือนที่กังวลไว้แต่ก่อน
      clearRecoverableUiState();
      const last = Number(sessionStorage.getItem(RELOAD_GUARD_KEY) ?? 0);
      if (Date.now() - last > 10000) {
        sessionStorage.setItem(RELOAD_GUARD_KEY, String(Date.now()));
        this.setState({ autoRemounting: true });
        setTimeout(() => window.location.reload(), 400);
      }
    }
  }
  render() {
    if (this.state.autoRemounting) {
      return (
        <div style={{
          display:"flex", flexDirection:"column", alignItems:"center", justifyContent:"center",
          height:"100vh", gap:14, fontFamily:"Sarabun, sans-serif", background:"#F0F4F8",
        }}>
          <div style={{
            width:36, height:36, border:"4px solid #E0E6ED", borderTopColor:"#1B4F9B",
            borderRadius:"50%", animation:"spin 0.8s linear infinite",
          }} />
          <style>{`@keyframes spin { to { transform: rotate(360deg); } }`}</style>
          <span style={{ color:"#1B4F9B", fontSize:14 }}>กำลังรีเฟรชหน้าให้ค่ะ...</span>
        </div>
      );
    }
    if (this.state.error) {
      return (
        <div style={{
          display:"flex", flexDirection:"column", alignItems:"center", justifyContent:"center",
          height:"100vh", gap:14, padding:24, fontFamily:"Sarabun, sans-serif", background:"#F0F4F8",
        }}>
          <div style={{ fontSize:40 }}>⚠️</div>
          <div style={{ fontWeight:700, color:"#C62828", fontSize:16 }}>เกิดข้อผิดพลาดในการแสดงผล</div>
          <div style={{ color:"#888", fontSize:13, maxWidth:480, textAlign:"center" }}>
            ข้อมูลที่บันทึกไปแล้วไม่หายนะคะ ลองกดรีโหลดหน้าดูอีกครั้งค่ะ
          </div>
          <pre style={{
            maxWidth:720, maxHeight:140, overflow:"auto", margin:0, padding:"10px 12px",
            background:"#FFFFFF", border:"1px solid #DDE3ED", borderRadius:8, color:"#64748B",
            fontSize:11, whiteSpace:"pre-wrap", overflowWrap:"anywhere",
          }}>{this.state.error?.message || String(this.state.error || "")}</pre>
          <button onClick={() => {
            // เคลียร์ MSAL interaction lock ก่อนเสมอ แล้ว reload จริง
            // (remount เฉยๆไม่รีเซ็ต MsalProvider ที่อยู่เหนือ ErrorBoundary)
            clearRecoverableUiState();
            window.location.reload();
          }} style={{
            background:"#1B4F9B", color:"#fff", border:"none", borderRadius:8,
            padding:"10px 28px", cursor:"pointer", fontFamily:"Sarabun", fontSize:15,
          }}>🔄 รีโหลดหน้า</button>
        </div>
      );
    }
    return this.props.children;
  }
}

// LegacyLayout — wraps the kept-as-is modules (Secretary Room / Calendar /
// People) with DataProvider (they still need SharePoint/Graph data) and the
// same PmLayout shell as the Project pages, just as a separate layout
// instance since these don't need DataProvider's SharePoint fetches.
function LegacyLayout() {
  return (
    <DataProvider>
      <PmLayout />
    </DataProvider>
  );
}

export default function App() {
  const isAuthenticated = useIsAuthenticated();
  const location = useLocation();
  const { inProgress, instance } = useMsal();
  const [authError, setAuthError] = useState(null);

  // จับ error จาก redirect callback
  // MSAL ยิง event นี้แบบ async ได้ทุกเมื่อ (เช่น silent token renewal เบื้องหลัง)
  // ถ้าจังหวะนั้นมี component อื่นกำลัง render อยู่พอดี (เช่นตอน save+refresh
  // ข้อมูลในตารางรวมงาน) เรียก setState ตรงนี้ทันทีจะชนกัน → React error #300
  // ("Cannot update a component while rendering a different component")
  // แก้ด้วยการเลื่อน setState ออกไปนอก call stack ปัจจุบันด้วย setTimeout(0)
  useEffect(() => {
    const callbackId = instance.addEventCallback((event) => {
      if (event.eventType === EventType.LOGIN_FAILURE) {
        const err = event.error;
        if (err) setTimeout(() => setAuthError(`${err.errorCode}: ${err.errorMessage}`), 0);
      }
      if (event.eventType === EventType.LOGIN_SUCCESS) {
        setTimeout(() => setAuthError(null), 0);
      }
    });
    return () => instance.removeEventCallback(callbackId);
  }, [instance]);

  // กำลังประมวลผล redirect
  if (inProgress === InteractionStatus.HandleRedirect || inProgress === InteractionStatus.Login) {
    return (
      <div style={{
        display: "flex", flexDirection: "column",
        alignItems: "center", justifyContent: "center",
        height: "100vh", gap: 16,
        fontFamily: "Sarabun, sans-serif", color: "#1B4F9B",
        background: "#F0F4F8",
      }}>
        <img src="/DTC_logo.png" alt="DTC" style={{ height: 60, marginBottom: 8 }} />
        <div style={{
          width: 44, height: 44,
          border: "4px solid #E0E6ED",
          borderTopColor: "#1B4F9B",
          borderRadius: "50%",
          animation: "spin 0.8s linear infinite",
        }} />
        <style>{`@keyframes spin { to { transform: rotate(360deg); } }`}</style>
        <span style={{ fontSize: 16, fontWeight: 600 }}>กำลังเข้าสู่ระบบ...</span>
      </div>
    );
  }

  if (authError) {
    return (
      <div style={{
        display: "flex", flexDirection: "column",
        alignItems: "center", justifyContent: "center",
        height: "100vh", gap: 16, padding: 24,
        fontFamily: "Sarabun, sans-serif", background: "#F0F4F8",
      }}>
        <img src="/DTC_logo.png" alt="DTC" style={{ height: 60 }} />
        <div style={{
          background: "#FEECEC", border: "1px solid #F5C6C6",
          borderRadius: 12, padding: "20px 28px",
          maxWidth: 560, width: "100%",
        }}>
          <div style={{ fontWeight: 700, color: "#C62828", marginBottom: 8, fontSize: 16 }}>
            ❌ เข้าสู่ระบบไม่สำเร็จ
          </div>
          <div style={{ color: "#C62828", fontSize: 13, fontFamily: "monospace", wordBreak: "break-all" }}>
            {authError}
          </div>
          <div style={{ marginTop: 12, fontSize: 13, color: "#555" }}>
            <strong>วิธีแก้ที่พบบ่อย:</strong><br />
            1. ตรวจสอบ Redirect URI ใน Azure AD ว่าตรงกับ https://wmm.jg-innovation.net พอดี<br />
            2. ตรวจสอบ Authority / Tenant domain ใน authConfig.js
          </div>
        </div>
        <button
          onClick={() => { setAuthError(null); instance.loginRedirect(); }}
          style={{
            background: "#1B4F9B", color: "#fff", border: "none",
            borderRadius: 8, padding: "10px 28px", cursor: "pointer",
            fontFamily: "Sarabun, sans-serif", fontSize: 15,
          }}
        >
          ลองใหม่
        </button>
      </div>
    );
  }

  if (location.pathname.startsWith('/followup-test/')) return <ErrorBoundary><Suspense fallback={<RouteFallback />}><Routes><Route path="/followup-test/:id" element={<FollowUpTestPage />} /></Routes></Suspense></ErrorBoundary>;

  if (!isAuthenticated && location.pathname.startsWith("/live-report/follow-up/")) {
    return (
      <ErrorBoundary>
        <Suspense fallback={<RouteFallback />}>
          <Routes>
            <Route path="/live-report/follow-up/:jobId" element={<LiveReportFollowUpUpdatePage />} />
            <Route path="*" element={<Navigate to="/projects" replace />} />
          </Routes>
        </Suspense>
      </ErrorBoundary>
    );
  }

  if (!isAuthenticated) return <LoginPage />;

  return (
    <ErrorBoundary>
      <Suspense fallback={<RouteFallback />}>
      <Routes>
        <Route path="/" element={<Navigate to="/projects" replace />} />

        {/* one shared PmLayout instance — sidebar/topbar never remount while
            navigating between these, which is what was causing the flicker */}
        <Route element={<PmLayout />}>
          <Route path="/projects" element={<ProjectHomePage />} />
          <Route path="/projects/archive" element={<ArchivedProjectsPage />} />
          <Route path="/templates" element={<TemplatesPage />} />
          <Route path="/teams" element={<TeamsSettingsPage />} />
          <Route path="/resource-workload" element={<ResourceWorkloadPage />} />
          <Route path="/resource-settings" element={<ResourceSettingsPage />} />
          <Route path="/pre-project-control" element={<Navigate to="/pre-project-control/dashboard" replace />} />
          <Route path="/pre-project-control/dashboard" element={<PreProjectControlPage view="dashboard" />} />
          <Route path="/pre-project-control/list" element={<PreProjectControlPage view="list" />} />
          <Route path="/pre-project-control/cancelled" element={<PreProjectControlPage view="cancelled" />} />
          <Route path="/projects/:projectId/overview" element={<ProjectOverviewPage />} />
          <Route path="/projects/:projectId/backlog" element={<BacklogPage />} />
          <Route path="/projects/:projectId/board" element={<BoardPage />} />
          <Route path="/projects/:projectId/sprints" element={<SprintPlanningPage />} />
          <Route path="/projects/:projectId/gantt" element={<GanttPage />} />
          <Route path="/projects/:projectId/schedule-import" element={<ScheduleImportPage />} />
          <Route path="/projects/:projectId/budget" element={<BudgetPage />} />
          <Route path="/projects/:projectId/budget/report" element={<BudgetReportPage />} />
          <Route path="/projects/:projectId/project-settings" element={<ProjectSettingsPage />} />
          <Route path="/projects/:projectId/audit-log" element={<AuditLogPage />} />
          <Route path="/projects/:projectId/workflow" element={<WorkflowSettingsPage />} />
          <Route path="/projects/:projectId/workload" element={<WorkloadPage />} />
          <Route path="/projects/:projectId/automation" element={<AutomationSettingsPage />} />
          <Route path="/projects/:projectId/permissions" element={<PermissionsSettingsPage />} />
          <Route path="/me/dashboard" element={<MyDashboardPage />} />
          <Route path="/me/work-calendar" element={<MyCalendarPage />} />
          <Route path="/me/tasks" element={<MyTasksPage />} />
          <Route path="/me/settings" element={<MySettingsPage />} />
        </Route>

        <Route element={<LegacyLayout />}>
          <Route path="/secretary-room" element={<SecretaryRoom />} />
          <Route path="/calendar" element={<TeamCalendar />} />
          <Route
            path="/demo-dashboard"
            element={<DemoWorkflowPage initialTab="dashboard" visibleTabIds={["dashboard"]} pageTitle="Dashboard" showTabs={false} showHeader={false} />}
          />
          <Route
            path="/demo-workflow"
            element={<DemoWorkflowPage initialTab="summary" visibleTabIds={["summary", "board", "list", "completed", "follow-up", "mail-confirm", "audit", "settings", "admin"]} pageTitle="Demo workflow" />}
          />
          <Route
            path="/demo-installation-report"
            element={<DemoWorkflowPage initialTab="installation-report" visibleTabIds={["installation-report"]} pageTitle="รายงานการติดตั้งทดลอง" showTabs={false} showHeader={false} />}
          />
          <Route path="/sales-import" element={<SalesImportPage />} />
          <Route path="/sales-import/customers" element={<CustomerDetailPage />} />
          <Route path="/sales-import/analytics" element={<SalesAnalyticsPage />} />
          <Route path="/sales-import/products" element={<ProductPerformancePage />} />
          <Route path="/sales-import/audit" element={<SalesImportAuditPage />} />
          <Route path="/people" element={<PeopleAdminPage />} />
          <Route path="/suppliers" element={<SupplierPoolPage />} />
          <Route path="/live-report" element={<LiveReportLayout />}>
            <Route index element={<Navigate to="/live-report/request-calendar" replace />} />
            <Route path="request-calendar" element={<ReceivedWorkCalendar />} />
            <Route path="statistics" element={<FollowUpStatistics />} />
            <Route path="dashboard" element={<Navigate to="/live-report/statistics?tab=dashboard" replace />} />
            <Route path="person-requests" element={<Navigate to="/live-report/statistics?tab=person-requests" replace />} />
            <Route path="person-support" element={<Navigate to="/live-report/statistics?tab=person-support" replace />} />
            <Route path="work-overview" element={<Navigate to="/live-report/statistics?tab=work-overview" replace />} />
            <Route path="management-type" element={<Navigate to="/live-report/statistics?tab=management-type" replace />} />
            <Route
              path="on-track"
              element={<TaskTablePage title="On Track Tasks" filterFn={(item) => gf(item, "managementType") === MANAGEMENT_ON_TRACK} />}
            />
            <Route
              path="push"
              element={<TaskTablePage title="Growth Project Tasks" filterFn={(item) => gf(item, "managementType") === MANAGEMENT_GROWTH} />}
            />
            <Route
              path="strategic"
              element={<TaskTablePage title="Strategic Project Tasks" filterFn={(item) => gf(item, "managementType") === MANAGEMENT_STRATEGIC} />}
            />
            <Route path="all-tasks" element={<Navigate to="/live-report/active-tasks" replace />} />
            <Route path="active-tasks" element={<AllTasksPage variant="active" />} />
            <Route path="cold-tasks" element={<AllTasksPage variant="cold" />} />
            <Route path="follow-up/:jobId" element={<LiveReportFollowUpUpdatePage />} />
            <Route path="settings" element={<LiveReportSettingsPage />} />
          </Route>
        </Route>

        <Route path="*" element={<Navigate to="/projects" replace />} />
      </Routes>
      </Suspense>
    </ErrorBoundary>
  );
}
